import React, { useState } from 'react';
import {
  Typography,
  Card,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Row,
  Col,
  Statistic,
  Empty,
  Tooltip,
  Modal,
  Form,
  message,
  Popconfirm
} from 'antd';
import {
  HistoryOutlined,
  InboxOutlined,
  WarningOutlined,
  SyncOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, PageLayout } from '../../ui';
import {
  useGetAllWarehousesQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  type WarehouseWithStats,
  type CreateWarehouseRequest,
  type UpdateWarehouseRequest,
} from '../../store/api/warehousesApi';
import { useGetCargoOnWarehouseQuery } from '../../store/api/cargoApi';
import { ApplicationCargo, CARGO_STATUS_LABELS, CARGO_STATUS_COLORS } from '../../types';
import dayjs from 'dayjs';
import WarehouseEditModal from '../../components/warehouses/WarehouseEditModal';
import WarehouseViewModal from '../../components/warehouses/WarehouseViewModal';
import CargoHistoryModal from '../../components/cargo/CargoHistoryModal';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';

const { Text } = Typography;
const { Option } = Select;
const { Search } = Input;
// TextArea currently not used in this file
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { TextArea } = Input;

interface CargoFilters {
  search?: string;
  applicationNumber?: string;
  status?: string;
}

interface WarehouseFormValues {
  name: string;
  city: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
}

const WarehousesPage: React.FC = () => {
  const [activeWarehouseId, setActiveWarehouseId] = useState<number | null>(null);
  const [filters, setFilters] = useState<CargoFilters>({});
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<ApplicationCargo | null>(null);
  
  // Modal states
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseWithStats | null>(null);
  const [form] = Form.useForm();


  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  // API queries
  const { data: warehousesData = [], isLoading: warehousesLoading, refetch: refetchWarehouses } = useGetAllWarehousesQuery();
  const [createWarehouse, { isLoading: isCreating }] = useCreateWarehouseMutation();
  const [updateWarehouse, { isLoading: isUpdating }] = useUpdateWarehouseMutation();
  const [deleteWarehouse] = useDeleteWarehouseMutation();

  // Получаем грузы для текущего склада
  const { data: cargoData = [], isLoading: cargoLoading, refetch: refetchCargo } = useGetCargoOnWarehouseQuery(activeWarehouseId!, {
    skip: !activeWarehouseId
  });

  // Устанавливаем первый склад как активный по умолчанию
  React.useEffect(() => {
    if (warehousesData.length > 0 && !activeWarehouseId) {
      setActiveWarehouseId(warehousesData[0].id);
    }
  }, [warehousesData, activeWarehouseId]);

  // Получаем текущий склад
  const currentWarehouse = warehousesData.find(w => w.id === activeWarehouseId);

  // Функция для определения длительности хранения
  const getStorageDuration = (createdAt: string): number => {
    return dayjs().diff(dayjs(createdAt), 'days');
  };

  // CRUD обработчики для складов
  const handleCreateWarehouse = async (values: WarehouseFormValues) => {
    try {
      const request: CreateWarehouseRequest = {
        name: values.name.trim(),
        city: values.city.trim(),
        address: values.address?.trim(),
        contactPerson: values.contactPerson?.trim(),
        phone: values.phone?.trim()
      };

      await createWarehouse(request).unwrap();
      message.success('Склад успешно создан');
      setIsCreateModalVisible(false);
      form.resetFields();
      refetchWarehouses();
    } catch (error: any) {
      message.error(error?.data?.error || 'Ошибка при создании склада');
    }
  };

  const handleEditWarehouse = async (values: WarehouseFormValues) => {
    if (!editingWarehouse) return;

    try {
      const request: UpdateWarehouseRequest = {
        id: editingWarehouse.id,
        name: values.name.trim(),
        city: values.city.trim(),
        address: values.address?.trim(),
        contactPerson: values.contactPerson?.trim(),
        phone: values.phone?.trim()
      };

      await updateWarehouse(request).unwrap();
      message.success('Склад успешно обновлен');
      setIsEditModalVisible(false);
      setEditingWarehouse(null);
      form.resetFields();
      refetchWarehouses();
    } catch (error: any) {
      message.error(error?.data?.error || 'Ошибка при обновлении склада');
    }
  };

  const handleDeleteWarehouse = async (warehouse: WarehouseWithStats) => {
    try {
      await deleteWarehouse(warehouse.id).unwrap();
      message.success(`Склад "${warehouse.name}" успешно удален`);
      
      // Если удаляемый склад был активным, переключаемся на первый доступный
      if (activeWarehouseId === warehouse.id) {
        const remainingWarehouses = warehousesData.filter(w => w.id !== warehouse.id);
        setActiveWarehouseId(remainingWarehouses.length > 0 ? remainingWarehouses[0].id : null);
      }
      
      refetchWarehouses();
    } catch (error: any) {
      message.error(error?.data?.error || 'Ошибка при удалении склада');
    }
  };

  const openEditModal = (warehouse: WarehouseWithStats) => {
    setEditingWarehouse(warehouse);
    form.setFieldsValue({
      name: warehouse.name,
      city: warehouse.city,
      address: warehouse.address,
      contactPerson: warehouse.contactPerson,
      phone: warehouse.phone
    });
    setIsEditModalVisible(true);
  };

  // Обработчики фильтрации
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key: keyof CargoFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleShowHistory = (cargo: ApplicationCargo) => {
    setSelectedCargo(cargo);
    setIsHistoryModalVisible(true);
  };

  // Фильтрация данных
  const filteredCargo = cargoData.filter(cargo => {
    if (filters.search && !cargo.name.toLowerCase().includes(filters.search.toLowerCase()) &&
        !cargo.description?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    if (filters.applicationNumber && 
        !cargo.application?.internalNumber.includes(filters.applicationNumber)) {
      return false;
    }

    if (filters.status && cargo.status !== filters.status) {
      return false;
    }

    return true;
  });

  // Колонки таблицы складов
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const warehouseColumns: ColumnsType<WarehouseWithStats> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city',
      sorter: (a, b) => a.city.localeCompare(b.city),
      render: (text: string) => (
        <Space>
          <EnvironmentOutlined />
          {text}
        </Space>
      )
    },
    {
      title: 'Адрес',
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => text || '—'
    },
    {
      title: 'Контактное лицо',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      render: (text: string) => text ? (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ) : '—'
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string) => text ? (
        <Space>
          <PhoneOutlined />
          {text}
        </Space>
      ) : '—'
    },
    {
      title: 'Грузов',
      dataIndex: ['_count', 'cargoOnWarehouse'],
      key: 'cargoCount',
      width: 80,
      align: 'center',
      sorter: (a, b) => a._count.cargoOnWarehouse - b._count.cargoOnWarehouse,
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>
          {count}
        </Tag>
      )
    },
    {
      title: 'Детали',
      key: 'details',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setActiveWarehouseId(record.id);
            setIsDetailsModalVisible(true);
          }}
        >
          Детали
        </Button>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Удаление склада"
            description={`Вы уверены, что хотите удалить склад "${record.name}"?`}
            onConfirm={() => handleDeleteWarehouse(record)}
            okText="Да"
            cancelText="Нет"
            disabled={record._count.cargoOnWarehouse > 0}
          >
            <Tooltip title={record._count.cargoOnWarehouse > 0 ? 'Нельзя удалить склад с грузами' : 'Удалить'}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={record._count.cargoOnWarehouse > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Колонки таблицы грузов (сокращенная версия)

  const cargoColumns: ColumnsType<ApplicationCargo> = [
    {
      title: 'Наименование груза',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.description}
              </Text>
            </div>
          )}
          {getStorageDuration(record.createdAt) > 14 && (
            <div style={{ marginTop: '4px' }}>
              <Tag 
                color={getStorageDuration(record.createdAt) > 30 ? 'red' : 'orange'}
                icon={<WarningOutlined />}
              >
                {getStorageDuration(record.createdAt)} дней
              </Tag>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (value: number) => <Text strong>{value}</Text>
    },
    {
      title: 'Вес (кг)',
      dataIndex: 'totalWeightKg',
      key: 'totalWeightKg',
      width: 100,
      align: 'center',
      sorter: (a, b) => Number(a.totalWeightKg) - Number(b.totalWeightKg),
      render: (value: number) => <Text>{Number(value).toLocaleString('ru-RU')}</Text>
    },
    {
      title: 'Объем (м³)',
      dataIndex: 'volumeM3',
      key: 'volumeM3',
      width: 100,
      align: 'center',
      sorter: (a, b) => Number(a.volumeM3 || 0) - Number(b.volumeM3 || 0),
      render: (value: number) => <Text>{value ? Number(value).toFixed(3) : '—'}</Text>
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 120,
      render: (_, record) => {
        const app = record.application;
        return (
          <div>
            <Text strong>№{app?.internalNumber}</Text>
            {(app && (app.externalNumber || app.applicationDate)) && (
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ({app.externalNumber || '—'}{app.applicationDate ? ` от ${dayjs(app.applicationDate).format('DD.MM.YYYY')}` : ''})
                </Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Клиент',
      key: 'client',
      width: 150,
      sorter: (a, b) => {
        const clientA = a.application?.client?.name || '';
        const clientB = b.application?.client?.name || '';
        return clientA.localeCompare(clientB);
      },
      render: (_, record) => {
        if (record.application?.client) {
          return (
            <div>
              <Text strong>{record.application.client.name}</Text>
              {record.application.client.contactPerson && (
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {record.application.client.contactPerson}
                  </Text>
                </div>
              )}
            </div>
          );
        }
        return <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'Дата прихода',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (_: any, record: any) => {
        // Prefer explicit deliveredDate (final delivery), then updatedAt (last status change), then createdAt
        const arrival = record.deliveredDate || record.updatedAt || record.createdAt;
        return (
          <Text>{arrival ? dayjs(arrival).format('DD.MM.YYYY HH:mm') : '—'}</Text>
        );
      }
    },
    {
      title: 'Маршрут прибытия',
      key: 'arrivalRoute',
      width: 130,
      render: (_, record) => {
        // Если груз в маршруте - показываем текущий маршрут
        if (record.currentRoute) {
          return (
            <div>
              <Text strong>№{record.currentRoute.id}</Text>
              <div>
                <Tag color="green">
                  {record.currentRoute.transportType?.toUpperCase()}
                </Tag>
              </div>
            </div>
          );
        }
        
        // Если груз на складе - показываем информацию о том, как он сюда попал
        if (record.currentWarehouse) {
          // Проверяем, есть ли связанные маршруты в заявке
          if (record.application?.routes && record.application.routes.length > 0) {
            // Берем последний маршрут (самый актуальный)
            const lastRoute = record.application.routes[0]; // Уже отсортированы по дате
            return (
              <div>
                <Text strong>№{lastRoute.route.id}</Text>
                <div>
                  <Tag color="blue">
                    {lastRoute.route.transportType?.toUpperCase()}
                  </Tag>
                </div>
              </div>
            );
          }
          
          // Если нет маршрутов, но есть заявка - показываем информацию о заявке
          if (record.application) {
            return (
              <div>
                <Text type="secondary">Из заявки</Text>
                <div>
                  <Text strong>№{record.application.internalNumber}</Text>
                </div>
              </div>
            );
          }
        }
        
        // Fallback - прямая поставка
        return <Text type="secondary">Прямая поставка</Text>;
      }
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => (
        <Tag color={CARGO_STATUS_COLORS[status as keyof typeof CARGO_STATUS_COLORS]}>
          {CARGO_STATUS_LABELS[status as keyof typeof CARGO_STATUS_LABELS] || status}
        </Tag>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Tooltip title="История">
          <Button
            type="text"
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => handleShowHistory(record)}
          />
        </Tooltip>
      )
    }
  ];

  // Статистика для текущего склада
  const warehouseStats = currentWarehouse ? {
    totalCargo: filteredCargo.length,
    totalWeight: filteredCargo.reduce((sum, cargo) => sum + Number(cargo.totalWeightKg), 0),
    totalVolume: filteredCargo.reduce((sum, cargo) => sum + Number(cargo.volumeM3 || 0), 0),
    longTermStorage: filteredCargo.filter(cargo => getStorageDuration(cargo.createdAt) > 14).length
  } : null;

  // Общая статистика по всем складам
  const totalStats = React.useMemo(() => {
    return warehousesData.reduce(
      (acc, w) => {
        acc.totalCargo += w._count?.cargoOnWarehouse || 0;
        acc.totalWeight += Number(w.totalWeight || 0);
        acc.totalVolume += Number(w.totalVolume || 0);
        return acc;
      },
      { totalCargo: 0, totalWeight: 0, totalVolume: 0 }
    );
  }, [warehousesData]);

  if (warehousesLoading) {
    return (
      <PageLayout title="Управление складами">
        <Card loading />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Управление складами"
      subtitle="Мониторинг грузов на складах"
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)}>
          Добавить склад
        </Button>
      }
    >
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Всего грузов на складах"
              value={totalStats.totalCargo}
              prefix={<InboxOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Общий вес"
              value={totalStats.totalWeight}
              suffix="кг"
              precision={0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Общий объём"
              value={totalStats.totalVolume}
              suffix="м³"
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Сводка по складам */}
      <Card
        title="Сводка по складам"
        style={{ marginBottom: '24px', backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}
        extra={
          <Button
            icon={<SyncOutlined />}
            onClick={() => refetchWarehouses()}
            loading={warehousesLoading}
            size="small"
          >
            Обновить
          </Button>
        }
      >
        <DataTable
          dataSource={warehousesData}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            {
              title: 'Склад',
              dataIndex: 'name',
              key: 'name',
              render: (text: string, record: WarehouseWithStats) => (
                <Space>
                  <EnvironmentOutlined />
                  <Text strong>{text}</Text>
                  {record.city && <Text type="secondary">({record.city})</Text>}
                </Space>
              )
            },
            {
              title: 'Грузов',
              dataIndex: ['_count', 'cargoOnWarehouse'],
              key: 'cargoCount',
              width: 100,
              align: 'center',
              sorter: (a: WarehouseWithStats, b: WarehouseWithStats) => (a._count?.cargoOnWarehouse || 0) - (b._count?.cargoOnWarehouse || 0),
              render: (count: number) => (
                <Tag color={count > 0 ? 'blue' : 'default'}>{count || 0}</Tag>
              )
            },
            {
              title: 'Вес (кг)',
              dataIndex: 'totalWeight',
              key: 'totalWeight',
              width: 120,
              align: 'right',
              sorter: (a: WarehouseWithStats, b: WarehouseWithStats) => Number(a.totalWeight || 0) - Number(b.totalWeight || 0),
              render: (val: number) => <Text>{Number(val || 0).toLocaleString('ru-RU')}</Text>
            },
            {
              title: 'Объём (м³)',
              dataIndex: 'totalVolume',
              key: 'totalVolume',
              width: 120,
              align: 'right',
              sorter: (a: WarehouseWithStats, b: WarehouseWithStats) => Number(a.totalVolume || 0) - Number(b.totalVolume || 0),
              render: (val: number) => <Text>{Number(val || 0).toFixed(2)}</Text>
            },
            {
              title: '',
              key: 'action',
              width: 100,
              render: (_: any, record: WarehouseWithStats) => (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setActiveWarehouseId(record.id)}
                >
                  Подробнее
                </Button>
              )
            }
          ]}
        />
      </Card>

      {/* Выбор склада */}
      <Card style={{ marginBottom: '24px', backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <Row gutter={16} align="middle" wrap>
          <Col span={8}>
            <Select
              placeholder="Выберите склад для просмотра"
              value={activeWarehouseId}
              onChange={setActiveWarehouseId}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {warehousesData.map((warehouse) => (
                <Option key={warehouse.id} value={warehouse.id}>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{warehouse.name}</span>
                    {warehouse.address && (
                      <span style={{ color: 'var(--text-secondary)' }}>— {warehouse.address}</span>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Button 
              icon={<SyncOutlined />} 
              onClick={() => refetchWarehouses()}
              loading={warehousesLoading}
            >
              Обновить
            </Button>
          </Col>
        </Row>
      </Card>

      {activeWarehouseId && currentWarehouse ? (
        <div>
          {/* Статистика склада */}
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card size="small" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
                <Statistic
                  title="Грузов"
                  value={warehouseStats?.totalCargo ?? 0}
                  prefix={<InboxOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
                <Statistic
                  title="Общий вес"
                  value={warehouseStats?.totalWeight ?? 0}
                  suffix="кг"
                  precision={0}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
                <Statistic
                  title="Объем"
                  value={warehouseStats?.totalVolume ?? 0}
                  suffix="м³"
                  precision={2}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
                <Statistic
                  title="Долго"
                  value={warehouseStats?.longTermStorage ?? 0}
                  valueStyle={{ color: (warehouseStats?.longTermStorage ?? 0) > 0 ? '#ff4d4f' : '#3f8600' }}
                  prefix={<WarningOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Фильтры */}
          <Card size="small" style={{ marginBottom: '16px', backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Search
                  placeholder="Поиск по наименованию"
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={6}>
                <Input
                  placeholder="Номер заявки"
                  allowClear
                  onChange={(e) => handleFilterChange('applicationNumber', e.target.value)}
                  value={filters.applicationNumber}
                />
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Статус"
                  allowClear
                  style={{ width: '100%' }}
                  onChange={(value) => handleFilterChange('status', value)}
                  value={filters.status}
                >
                  {Object.entries(CARGO_STATUS_LABELS).map(([key, label]) => (
                    <Option key={key} value={key}>{label}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Button onClick={handleClearFilters} block>
                  Сбросить
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Таблица грузов */}
          <Card
            title={`Грузы на складе "${currentWarehouse.name}"`}
            extra={
              <Button
                icon={<SyncOutlined />}
                onClick={() => refetchCargo()}
                loading={cargoLoading}
                size="small"
              >
                Обновить
              </Button>
            }
            size="small"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}
          >
            <DataTable
              columns={cargoColumns}
              dataSource={filteredCargo}
              rowKey="id"
              loading={cargoLoading}
              pagination={{
                pageSize: 10,
                size: 'small',
                showSizeChanger: false,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} из ${total}`,
              }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    description={`На складе "${currentWarehouse.name}" нет грузов`}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )
              }}
            />
          </Card>
        </div>
      ) : (
        <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
          <Empty description="Выберите склад для просмотра информации" />
        </Card>
      )}

      {/* Модальное окно создания склада */}
      <Modal
        title="Создать склад"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateWarehouse}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Название склада"
                rules={[{ required: true, message: 'Введите название склада' }]}
              >
                <Input placeholder="Склад_СПБ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label="Город"
                rules={[{ required: true, message: 'Введите город' }]}
              >
                <Input placeholder="Санкт-Петербург" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="address"
            label="Полный адрес"
          >
            <AddressAutocomplete placeholder="Введите полный адрес склада" style={{ width: '100%' }} />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contactPerson"
                label="Контактное лицо"
              >
                <Input placeholder="Иванов И.И." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Телефон"
              >
                <Input placeholder="+7 (812) 123-45-67" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsCreateModalVisible(false);
                form.resetFields();
              }}>
                Отмена
              </Button>
              <Button type="primary" htmlType="submit" loading={isCreating}>
                Создать
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно редактирования склада */}
      <Modal
        title="Редактировать склад"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingWarehouse(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditWarehouse}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Название склада"
                rules={[{ required: true, message: 'Введите название склада' }]}
              >
                <Input placeholder="Склад_СПБ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label="Город"
                rules={[{ required: true, message: 'Введите город' }]}
              >
                <Input placeholder="Санкт-Петербург" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="address"
            label="Полный адрес"
          >
            <AddressAutocomplete placeholder="Введите полный адрес склада" style={{ width: '100%' }} />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contactPerson"
                label="Контактное лицо"
              >
                <Input placeholder="Иванов И.И." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Телефон"
              >
                <Input placeholder="+7 (812) 123-45-67" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsEditModalVisible(false);
                setEditingWarehouse(null);
                form.resetFields();
              }}>
                Отмена
              </Button>
              <Button type="primary" htmlType="submit" loading={isUpdating}>
                Сохранить
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно истории перемещений */}
      <CargoHistoryModal
        visible={isHistoryModalVisible}
        onClose={() => setIsHistoryModalVisible(false)}
        cargoId={selectedCargo?.id || 0}
        cargoName={selectedCargo?.name}
      />

      {/* Модальное окно просмотра деталей склада */}
      <WarehouseViewModal
        visible={isViewModalVisible}
        warehouse={warehousesData.find(w => w.id === activeWarehouseId) || null}
        warehouseStats={warehouseStats}
        onClose={() => {
          setIsViewModalVisible(false);
        }}
        onEdit={() => {
          setIsViewModalVisible(false);
          setIsDetailsModalVisible(true);
        }}
      />

      {/* Модальное окно редактирования склада */}
      <WarehouseEditModal
        visible={isDetailsModalVisible}
        warehouse={warehousesData.find(w => w.id === activeWarehouseId) || null}
        onClose={() => {
          setIsDetailsModalVisible(false);
        }}
        onSave={async (updated) => {
          console.log('=== ОБРАБОТЧИК ONSAVE ===');
          console.log('Получены данные для сохранения:', updated);
          
          if (!updated.id || !updated.name || !updated.city) {
            console.error('Недостаточно данных для сохранения:', { id: updated.id, name: updated.name, city: updated.city });
            message.error('Название и город склада обязательны для заполнения');
            return;
          }
          
          try {
            const updateData = {
              id: updated.id,
              name: updated.name.trim(),
              city: updated.city.trim(),
              address: updated.address?.trim() || '',
              contactPerson: updated.contactPerson?.trim() || '',
              phone: updated.phone?.trim() || ''
            };
            console.log('Подготовленные данные для API:', updateData);
            
            console.log('Отправляем запрос через updateWarehouse...');
            const result = await updateWarehouse(updateData).unwrap();
            console.log('Результат API:', result);
            
            console.log('Закрываем модальное окно...');
            setIsDetailsModalVisible(false);
            
            console.log('Обновляем список складов...');
            refetchWarehouses();
            
            message.success('Склад успешно обновлен');
            console.log('=== СОХРАНЕНИЕ ЗАВЕРШЕНО ===');
          } catch (error: any) {
            console.error('=== ОШИБКА СОХРАНЕНИЯ ===');
            console.error('Полная ошибка:', error);
            console.error('Данные ошибки:', error?.data);
            console.error('Сообщение ошибки:', error?.message);
            message.error(error?.data?.error || error?.message || 'Ошибка при обновлении склада');
          }
        }}
      />
    </PageLayout>
  );
};

export default WarehousesPage; 